import * as React from 'react';
import { View } from 'react-native';
import Modal from 'react-native-modal';
import { createStyleSheet, useStyles } from 'react-native-unistyles';

import {
  NavigationButton,
  NavigationButtonTypes,
} from '@/components/navigation-button';
import type { TxKeyPath } from '@/core';
import { Button, Text } from '@/ui';

export interface DialogProps {
  isVisible: boolean;
  titleTx: TxKeyPath;
  bodyTx?: TxKeyPath;
  bodyTextStyle?: any;
  body?: React.ReactNode;
  leftButton?: {
    tx: TxKeyPath;
    onPress: () => void;
    style?: any;
  };
  rightButton: {
    tx: TxKeyPath;
    onPress: () => void;
  };
  onDismiss: () => void;
  style?: any;
  dismissable?: boolean;
}

export function ConfirmModal({
  isVisible = false,
  titleTx,
  bodyTx,
  bodyTextStyle,
  body,
  leftButton,
  rightButton,
  onDismiss,
  style,
  dismissable = true,
}: DialogProps) {
  const { styles, theme } = useStyles(stylesheet);
  const [visible, setVisible] = React.useState<boolean>(isVisible);

  React.useEffect(() => {
    setVisible(isVisible);
  }, [isVisible]);

  return (
    <Modal
      animationIn="slideInUp"
      animationInTiming={300}
      animationOut="slideOutDown"
      animationOutTiming={1000}
      isVisible={visible}
      hasBackdrop
      onBackdropPress={() => {
        dismissable && setVisible(false);
        onDismiss();
      }}
      onDismiss={() => {
        dismissable && setVisible(false);
        onDismiss();
      }}
    >
      <View
        style={{
          ...styles.container,
          ...style,
        }}
      >
        <View
          style={{
            width: '100%',
            gap: 40,
            backgroundColor: theme.colors.dialog.backgroundColor,
            padding: 16,
            borderRadius: 16,
          }}
        >
          <View
            style={{
              flexDirection: 'row',
              justifyContent: 'center',
            }}
          >
            <Text tx={titleTx} type="title2" />

            {dismissable ? (
              <NavigationButton
                type={NavigationButtonTypes.cancel}
                onPress={() => {
                  setVisible(false);
                  onDismiss();
                }}
                style={{ position: 'absolute', right: 0 }}
              />
            ) : null}
          </View>

          <View>
            {bodyTx ? (
              <Text
                tx={bodyTx}
                type="bodyRegular"
                style={[{ color: theme.colors.text.secondary }, bodyTextStyle]}
              />
            ) : (
              <View>{body}</View>
            )}
          </View>

          <View style={styles.footer}>
            {leftButton ? (
              <Button
                tx={leftButton.tx}
                type="secondary"
                onPress={leftButton.onPress}
                style={[styles.footerLeftButton, leftButton.style]}
              />
            ) : null}

            <Button
              tx={rightButton.tx}
              type="primary"
              onPress={rightButton.onPress}
              style={styles.footerRightButton}
            />
          </View>
        </View>
      </View>
    </Modal>
  );
}

const stylesheet = createStyleSheet((theme) => ({
  container: {
    margin: 0,
    alignItems: 'center',
    justifyContent: 'center',
    alignSelf: 'center',
    maxWidth: {
      xs: '100%',
      sm: 600,
      md: 800,
    },
    width: {
      xs: '100%',
      md: 700,
    },
  },
  footer: {
    flexDirection: {
      xs: 'column-reverse',
      md: 'row',
    },
    justifyContent: 'space-between',
    gap: 4,
  },
  footerLeftButton: {
    width: {
      xs: '100%',
      md: 250,
    },
  },
  footerRightButton: {
    width: {
      xs: '100%',
      md: 300,
    },
  },
}));
